/**
 * Pure-function validators for the user-facing "about" string and the
 * single-emoji status that ride alongside name and avatar inside the
 * profile-key-encrypted ProfileBlob.
 *
 * Threat model:
 *   - About text capped to 130 unicode scalars so the serialized JSON
 *     cannot balloon under an emoji-heavy or ZWJ-heavy payload. Length is
 *     counted via `[...str].length` (spread over code points), NOT
 *     `str.length` (UTF-16 code units) — the same gotcha as
 *     AttachmentCaption. Mirrors iOS `String.unicodeScalars.count`.
 *   - Emoji status restricted to a SINGLE unicode scalar with Emoji_Presentation
 *     property. ZWJ-joined sequences (e.g. family emoji) and VS16 forms
 *     ("❤️" = U+2764 + U+FE0F) are rejected as multipleScalars. Allowing
 *     multi-scalar sequences today would mean accepting an arbitrary-length
 *     sequence of scalars under the "it's just one emoji" banner — the exact
 *     ZWJ-text-injection vector we're closing.
 *
 * Mirrors: kaether_for_ios/Stoat/E2EE/ProfileStatusValidation.swift
 */

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type ProfileAboutValidationError =
  | { readonly kind: "tooLong"; readonly unicodeScalarCount: number }
  | { readonly kind: "emptyAfterTrim" };

export type ProfileEmojiValidationError =
  | { readonly kind: "multipleScalars"; readonly count: number }
  | { readonly kind: "notAnEmojiScalar" }
  | { readonly kind: "empty" };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Cap chosen to match Signal's about-text limit; measured in Unicode code
 *  points (scalars), not UTF-16 code units. */
export const ABOUT_MAX_UNICODE_SCALARS = 130;

// ---------------------------------------------------------------------------
// Emoji-presentation check
// ---------------------------------------------------------------------------

/**
 * Closest web analogue to iOS `Unicode.Scalar.Properties.isEmojiPresentation`.
 *
 * `\p{Emoji_Presentation}` matches scalars whose *default* presentation is
 * emoji (i.e. they render as emoji without a variation selector). This is
 * stricter than `\p{Emoji}` alone, which also flags bare digits ("1") because
 * they participate in keycap sequences — just as iOS's `isEmoji` does. We
 * require the stricter property, exactly mirroring iOS.
 *
 * The /u flag enables Unicode mode so \p{...} property escapes work.
 */
const EMOJI_PRESENTATION_RE = /^\p{Emoji_Presentation}$/u;

/**
 * Returns true iff the single code-point string `scalar` has Emoji_Presentation
 * as its default rendering. Callers guarantee `scalar` is exactly one code
 * point long (i.e. one element of `[...str]`).
 */
export function isEmojiScalar(scalar: string): boolean {
  return EMOJI_PRESENTATION_RE.test(scalar);
}

// ---------------------------------------------------------------------------
// About validator
// ---------------------------------------------------------------------------

/**
 * Trims whitespace from the input, then enforces the 130-scalar cap.
 *
 * Returns the trimmed string on success.
 * Returns `null` with `{ kind: "emptyAfterTrim" }` if the input is
 * whitespace-only after trim.
 * Returns `null` with `{ kind: "tooLong" }` if scalar count exceeds 130.
 *
 * Mirrors: ProfileStatusValidation.normaliseAbout(_:)
 */
export function validateAbout(
  raw: string,
): { ok: true; value: string } | { ok: false; error: ProfileAboutValidationError } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: { kind: "emptyAfterTrim" } };
  }
  // Spread to iterate over code points (unicode scalars), not UTF-16 units.
  const scalars = [...trimmed];
  const scalarCount = scalars.length;
  if (scalarCount > ABOUT_MAX_UNICODE_SCALARS) {
    return { ok: false, error: { kind: "tooLong", unicodeScalarCount: scalarCount } };
  }
  return { ok: true, value: trimmed };
}

// ---------------------------------------------------------------------------
// Emoji-status validator
// ---------------------------------------------------------------------------

/**
 * Trims whitespace, then asserts a single emoji-presentation unicode scalar.
 *
 * Returns the trimmed single-scalar emoji string on success.
 * Returns an error for:
 *   - empty-after-trim  → { kind: "empty" }
 *   - more than 1 scalar (ZWJ sequences, VS16 forms, etc.)
 *                       → { kind: "multipleScalars", count: N }
 *   - non-emoji single scalar (e.g. "A", digits)
 *                       → { kind: "notAnEmojiScalar" }
 *
 * Mirrors: ProfileStatusValidation.normaliseEmoji(_:)
 */
export function validateEmojiStatus(
  raw: string,
): { ok: true; value: string } | { ok: false; error: ProfileEmojiValidationError } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: { kind: "empty" } };
  }
  // Spread over code points to count scalars correctly. A ZWJ family like
  // U+1F468 U+200D U+1F469 U+200D U+1F467 gives 5 elements; "❤️" gives 2.
  const scalars = [...trimmed];
  if (scalars.length !== 1) {
    return {
      ok: false,
      error: { kind: "multipleScalars", count: scalars.length },
    };
  }
  if (!isEmojiScalar(scalars[0]!)) {
    return { ok: false, error: { kind: "notAnEmojiScalar" } };
  }
  return { ok: true, value: trimmed };
}
